import { EventEmitter, Injectable, Injector, Output } from '@angular/core';
import { AbstractWorkOrderDataService } from '@app/work-order/abstract-work-order-data.service';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { ChangeLogService } from '@app/core/services/change-log.service';
import { FeedbackControllerService, JobFeedbackControllerService } from '@app/generated/service';
import { JobFeedbackDto, JobFeedbackOptionsDto, JobFeedbackUpdateRequest } from '@app/generated/model';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { WO_CREATE_JOB_FEEDBACK } from '@app/actions';

export interface CreateJobFeedbackRequest {
  sentiment: JobFeedbackUpdateRequest.SentimentEnum;
  topics: string[];
  comment?: string;
  potentialImpact: JobFeedbackUpdateRequest.PotentialImpactEnum;
  potentialImpacts?: JobFeedbackUpdateRequest.PotentialImpactEnum[];
  performedBy?: string;
  suggestedImprovement?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WorkOrderFeedbackService extends AbstractWorkOrderDataService<JobFeedbackDto[]> {
  @Output() feedbackUpdated = new EventEmitter<void>();

  public feedback$ = new BehaviorSubject<JobFeedbackDto[]>([]);
  public options$ = new BehaviorSubject<JobFeedbackOptionsDto | null>(null);

  constructor(
    private readonly feedbackController: JobFeedbackControllerService,
    private readonly optionsController: FeedbackControllerService,
    protected changeLogService: ChangeLogService,
    injector: Injector,
  ) {
    super(injector, 'JOB_FEEDBACK');
    this.getData().subscribe((feedback) => this.feedback$.next(feedback ?? []));
  }

  initialize(workOrderId: string, parentData: any = null): void {
    const normalizedWorkOrderId = (workOrderId ?? '').trim();
    if (!normalizedWorkOrderId) {
      this.dispose();
      return;
    }

    super.initialize(normalizedWorkOrderId, parentData);
    if (!Array.isArray(this.get())) {
      this.setData([]);
    }
  }

  load(): void {
    const currentWorkOrderId = (this.workOrderId ?? '').trim();
    if (!currentWorkOrderId) {
      this.setData([]);
      return;
    }

    this.feedbackController.getFeedback(currentWorkOrderId, false).subscribe({
      next: (feedback) => this.setData(this.sortByLatest(feedback ?? [])),
      error: (error) => {
        console.warn(`[JOB_FEEDBACK] Failed to load feedback for ${this.workOrderId}.`, error);
        void this.handleLoadFailure([], error);
      },
    });
  }

  loadOptions(): void {
    if (this.options$.value) {
      return;
    }

    this.optionsController.getOptions().subscribe({
      next: (options) => this.options$.next(options),
      error: (error) => {
        console.warn('[JOB_FEEDBACK] Failed to load feedback options.', error);
        this.options$.next(null);
      },
    });
  }

  loadCount(workOrderId: string): Promise<number> {
    const normalizedWorkOrderId = (workOrderId ?? '').trim();
    if (!normalizedWorkOrderId) {
      return Promise.resolve(0);
    }

    return firstValueFrom(this.feedbackController.getFeedbackCount(normalizedWorkOrderId, false));
  }

  async createFeedback(request: CreateJobFeedbackRequest): Promise<void> {
    const currentWorkOrderId = (this.workOrderId ?? '').trim();
    if (!currentWorkOrderId) {
      throw new Error('Work order is not initialized for feedback.');
    }

    const payload = {
      id: uuid(),
      workOrderId: currentWorkOrderId,
      sentiment: request.sentiment,
      topics: request.topics,
      comment: request.comment,
      potentialImpact: request.potentialImpact,
      potentialImpacts: request.potentialImpacts,
      performedBy: request.performedBy,
      suggestedImprovement: request.suggestedImprovement,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await this.changeLogService.publish(WO_CREATE_JOB_FEEDBACK, payload, [currentWorkOrderId]);
    this.applyLocalChange(WO_CREATE_JOB_FEEDBACK, payload);
    this.feedbackUpdated.emit();
  }

  async updateFeedback(feedbackId: string, request: JobFeedbackUpdateRequest): Promise<void> {
    const currentWorkOrderId = (this.workOrderId ?? '').trim();
    if (!currentWorkOrderId) {
      throw new Error('Work order is not initialized for feedback.');
    }

    const updated = await firstValueFrom(
      this.feedbackController.updateFeedback(currentWorkOrderId, feedbackId, request),
    );
    const current = Array.isArray(this.get()) ? [...this.get()] : [];
    const index = current.findIndex((item) => item.id === feedbackId);

    if (index >= 0) {
      current[index] = updated;
    } else {
      current.unshift(updated);
    }

    this.setData(this.sortByLatest(current));
    this.feedbackUpdated.emit();
  }

  async resendFeedback(feedback: JobFeedbackDto): Promise<void> {
    const currentWorkOrderId = (this.workOrderId ?? '').trim();
    if (!currentWorkOrderId) {
      throw new Error('Work order is not initialized for feedback.');
    }

    const feedbackId = (feedback?.id ?? '').trim();
    if (!feedbackId) {
      throw new Error('Feedback id is missing.');
    }

    await firstValueFrom(this.feedbackController.resendFeedbackEmail(currentWorkOrderId, feedbackId));
    this.feedbackUpdated.emit();
  }

  protected apply(feedback: JobFeedbackDto[], event: ChangeLogEvent): void {
    if (event.type !== WO_CREATE_JOB_FEEDBACK) {
      return;
    }

    const list = Array.isArray(feedback) ? [...feedback] : [];
    const payload: any = event.content;

    if (!payload?.id || list.some((item) => item.id === payload.id)) {
      return;
    }

    list.unshift({
      id: payload.id,
      workOrderId: payload.workOrderId,
      sentiment: payload.sentiment,
      topics: Array.isArray(payload.topics) ? payload.topics : [],
      comment: payload.comment ?? null,
      potentialImpact: payload.potentialImpact,
      performedBy: payload.performedBy ?? null,
      suggestedImprovement: payload.suggestedImprovement ?? null,
      createdAt: payload.createdAt ?? new Date(event.created).toISOString(),
      updatedAt: payload.updatedAt ?? new Date(event.updated).toISOString(),
    });

    this.setData(this.sortByLatest(list));
  }

  private applyLocalChange(type: string, payload: unknown): void {
    const now = Date.now();
    const event: ChangeLogEvent = {
      key: `local-${uuid()}`,
      type,
      status: 'PENDING',
      content: payload,
      refs: this.workOrderId ? [this.workOrderId] : [],
      created: now,
      updated: now,
    };

    this.apply(this.get() ?? [], event);
  }

  private sortByLatest(feedback: JobFeedbackDto[]): JobFeedbackDto[] {
    return [...feedback].sort((a, b) => {
      const left = Date.parse(a.updatedAt ?? a.createdAt ?? '');
      const right = Date.parse(b.updatedAt ?? b.createdAt ?? '');
      return (Number.isNaN(right) ? 0 : right) - (Number.isNaN(left) ? 0 : left);
    });
  }
}
